import { pregnancyStickerUrl, stickerUrl } from "@listou/shared/stickers";
import { useState } from "react";

import { BABY_WEEKS, comparisonLabel, formatLength, formatWeight } from "../../lib/baby-weeks";
import { formatDay, todayInFrance } from "../../lib/format";
import { addDays, ageOn, daysBetween, dating } from "../../lib/pregnancy";
import { Sticker } from "../list-page/parts";
import { DateTile, DISCLAIMER, ISO_DAY } from "./due-date";
import { BabyThisWeek, PregnancyTimeline } from "./pregnancy-visuals";
import { ToolLayout, type ToolCopy } from "./tool-layout";

/** French term is 41 SA: the last period is 287 days before it. */
const FRENCH_TERM_DAYS = 287;

/** From the last period, or back from the due date the maternity gave. */
function PregnancyDateInput({ onChange }: { onChange: (lmp: string | null) => void }) {
  const [from, setFrom] = useState<"lmp" | "due">("lmp");
  const [value, setValue] = useState("");
  const update = (next: string, mode = from) => {
    setValue(next);
    onChange(
      ISO_DAY.test(next) ? (mode === "due" ? addDays(next, -FRENCH_TERM_DAYS) : next) : null,
    );
  };
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap gap-1">
        {(
          [
            ["lmp", "Dernières règles"],
            ["due", "Date prévue d'accouchement"],
          ] as const
        ).map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            className={from === mode ? "btn btn-sm" : "btn-ghost btn-sm"}
            onClick={() => {
              setFrom(mode);
              update(value, mode);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <input
        type="date"
        className="input"
        aria-label={
          from === "lmp" ? "Premier jour des dernières règles" : "Date prévue d'accouchement"
        }
        value={value}
        onChange={(event) => update(event.target.value)}
      />
    </div>
  );
}

// --- Semaines d'aménorrhée ----------------------------------------------------

export const SA_COPY: ToolCopy = {
  h1: "Calculer ses semaines d'aménorrhée",
  intro:
    "À combien de SA êtes-vous aujourd'hui ? Indiquez vos dernières règles ou la date prévue d'accouchement : vous voyez vos semaines d'aménorrhée, de grossesse, le mois en cours et la taille de bébé.",
  sections: [
    {
      heading: "SA, SG, mois : trois façons de compter",
      paragraphs: [
        "Les semaines d'aménorrhée (SA) partent du premier jour des dernières règles : c'est l'unité du suivi médical en France. Les semaines de grossesse (SG) partent de la fécondation, deux semaines plus tard. Les mois, eux, se comptent depuis le début de grossesse.",
        "Exemple : à 20 SA, vous êtes à 18 SG, au 5e mois de grossesse.",
      ],
    },
    {
      heading: "Les trimestres",
      paragraphs: [
        "En France, le premier trimestre va jusqu'à 15 SA environ, le deuxième jusqu'à 28 SA, et le troisième jusqu'au terme, à 41 SA. Chacun a son échographie : vers 12, 22 et 32 SA.",
      ],
    },
  ],
  faq: [
    {
      question: "Combien de SA dure une grossesse ?",
      answer:
        "41 SA selon le calcul français, 40 SA dans la plupart des autres pays. Un bébé est considéré à terme à partir de 37 SA.",
    },
    {
      question: "Comment passer des SA aux mois de grossesse ?",
      answer:
        "Retirez deux semaines pour obtenir les semaines de grossesse, puis comptez environ 4,3 semaines par mois. 12 SA correspondent ainsi à la fin du 3e mois.",
    },
  ],
};

export function SaCalculatorView() {
  const [lmp, setLmp] = useState<string | null>(null);
  return (
    <ToolLayout copy={SA_COPY} eyebrow="Calculateur gratuit" disclaimer={DISCLAIMER}>
      <PregnancyDateInput onChange={setLmp} />
      {lmp ? (
        <SaResult lmp={lmp} />
      ) : (
        <p className="text-center text-muted">Le résultat s'affiche dès la date saisie.</p>
      )}
    </ToolLayout>
  );
}

function SaResult({ lmp }: { lmp: string }) {
  const today = todayInFrance();
  const result = dating({ method: "lmp", lmp, cycleDays: 28 });
  const age = ageOn(result.amenorrhoeaStart, today);
  if (!age) {
    return (
      <p className="text-center text-muted" aria-live="polite">
        Cette date ne correspond pas à une grossesse en cours aujourd'hui.
      </p>
    );
  }
  const sinceStart = daysBetween(result.start, today);
  const month = Math.max(1, Math.floor(sinceStart / 30.44) + 1);
  const trimester = age.weeks < 15 ? 1 : age.weeks < 28 ? 2 : 3;
  return (
    <div className="grid gap-6 border-t-[1.5px] border-line pt-6" aria-live="polite">
      <div className="grid justify-items-center gap-1 text-center">
        <p className="font-bold text-muted">Aujourd'hui, vous êtes à</p>
        <p className="font-soft text-5xl font-medium text-accent-text sm:text-6xl">
          {age.weeks} SA{age.days > 0 && ` + ${age.days} j`}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <DateTile label="Semaines de grossesse" value={`${Math.max(age.weeks - 2, 0)} SG`} />
        <DateTile label="Mois de grossesse" value={`${month}${month === 1 ? "er" : "e"} mois`} />
        <DateTile
          label="Trimestre"
          value={`${trimester}${trimester === 1 ? "er" : "e"} trimestre`}
        />
      </div>
      <PregnancyTimeline result={result} todaySa={age.weeks * 7 + age.days} />
      <BabyThisWeek weeks={age.weeks} title="Cette semaine" />
      <p className="text-center text-sm text-muted">
        Terme prévu le {formatDay(result.dueDate)} (41 SA).
      </p>
    </div>
  );
}

// --- Semaine par semaine -----------------------------------------------------

export const WEEKS_COPY: ToolCopy = {
  h1: "La grossesse semaine par semaine",
  intro:
    "De la graine de sésame au potiron : la taille, le poids et les étapes du développement de bébé, de 5 à 41 SA. Indiquez vos dernières règles pour trouver votre semaine.",
  sections: [
    {
      heading: "Des mesures moyennes",
      paragraphs: [
        "Les tailles sont mesurées du sommet du crâne aux fesses jusqu'à 19 SA, puis du crâne aux talons : c'est pourquoi la taille fait un bond à 20 SA. Ce sont des moyennes, et chaque bébé grandit à son rythme.",
        "Jusqu'à 19 SA, bébé est comparé à un fruit de sa taille ; à partir de 20 SA, à un fruit ou un légume de son poids, plus parlant pour un bébé qui dépasse vite les 30 cm. Un ordre de grandeur, pas une mesure : votre sage-femme suit la croissance de bébé à chaque échographie.",
      ],
    },
    {
      heading: "Trois trimestres",
      paragraphs: [
        "Au premier trimestre, tous les organes se forment. Au deuxième, bébé grandit, bouge et commence à entendre. Au troisième, il prend du poids et se prépare à naître.",
      ],
    },
  ],
  faq: [
    {
      question: "Quand sent-on bébé bouger ?",
      answer:
        "Entre 16 et 22 SA en général, souvent plus tôt pour une deuxième grossesse. Les mouvements deviennent nets vers 20 à 24 SA.",
    },
    {
      question: "Combien pèse un bébé à la naissance ?",
      answer:
        "En moyenne autour de 3,3 kg à terme, pour une taille d'environ 50 cm. Entre 2,5 et 4 kg, le poids est considéré comme habituel.",
    },
  ],
};

const TRIMESTER_OF = (sa: number) => (sa < 15 ? 0 : sa < 28 ? 1 : 2);
const TRIMESTER_TITLES = ["1er trimestre", "2e trimestre", "3e trimestre"];

export function WeeksView() {
  const [lmp, setLmp] = useState<string | null>(null);
  const today = todayInFrance();
  const age = lmp ? ageOn(lmp, today) : null;
  return (
    <ToolLayout
      copy={WEEKS_COPY}
      eyebrow="Guide illustré"
      stickers={[pregnancyStickerUrl("fraise"), pregnancyStickerUrl("ananas")]}
      disclaimer={DISCLAIMER}
      after={<WeeksGallery current={age?.weeks ?? null} />}
    >
      <PregnancyDateInput onChange={setLmp} />
      {age ? (
        <BabyThisWeek weeks={age.weeks} title={`Vous êtes à ${age.weeks} SA`} />
      ) : (
        <p className="text-center text-muted">
          {lmp
            ? "Cette date ne correspond pas à une grossesse en cours."
            : "Ou parcourez toutes les semaines ci-dessous."}
        </p>
      )}
    </ToolLayout>
  );
}

function WeeksGallery({ current }: { current: number | null }) {
  return (
    <div className="grid gap-10">
      {TRIMESTER_TITLES.map((title, trimester) => (
        <section key={title} className="grid gap-4">
          <h2 className="text-3xl font-semibold">{title}</h2>
          <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {BABY_WEEKS.filter((week) => TRIMESTER_OF(week.sa) === trimester).map((week) => (
              <li
                key={week.sa}
                id={`sa-${week.sa}`}
                className={`grid scroll-mt-6 grid-cols-[auto_1fr] items-start gap-3 rounded-2xl border-[1.5px] p-4 ${
                  week.sa === current ? "border-accent bg-[#fde8ee]" : "border-line bg-surface"
                }`}
              >
                <Sticker src={pregnancyStickerUrl(week.sticker)} className="w-16" />
                <div className="grid gap-1">
                  <h3 className="font-sans text-lg font-bold">{week.sa} SA</h3>
                  <p className="text-sm">
                    {comparisonLabel(week)} · {formatLength(week.lengthCm)} ·{" "}
                    {formatWeight(week.weightG)}
                  </p>
                  <p className="text-sm text-ink/75">{week.note}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

// --- Ovulation ---------------------------------------------------------------

export const OVULATION_COPY: ToolCopy = {
  h1: "Calculer sa date d'ovulation",
  intro:
    "Indiquez le premier jour de vos dernières règles et la durée de votre cycle : vous obtenez vos prochaines dates d'ovulation et vos jours les plus fertiles, sur trois cycles.",
  sections: [
    {
      heading: "Comment se calcule l'ovulation ?",
      paragraphs: [
        "L'ovulation a lieu environ 14 jours avant les règles suivantes, quelle que soit la longueur du cycle. Avec un cycle de 28 jours, elle tombe vers le 14e jour ; avec un cycle de 32 jours, vers le 18e.",
        "Le calcul suppose un cycle régulier. Si le vôtre varie, les tests d'ovulation ou la courbe de température sont plus fiables.",
      ],
    },
    {
      heading: "La période de fertilité",
      paragraphs: [
        "Les spermatozoïdes survivent jusqu'à cinq jours, l'ovule environ un jour. Les chances de grossesse sont donc les plus élevées dans les cinq jours qui précèdent l'ovulation et le jour même.",
      ],
    },
  ],
  faq: [
    {
      question: "Quels sont les jours les plus fertiles ?",
      answer:
        "Les deux ou trois jours qui précèdent l'ovulation et le jour de l'ovulation lui-même.",
    },
    {
      question: "Peut-on ovuler avec un cycle irrégulier ?",
      answer:
        "Oui, mais la date est alors difficile à prévoir par le calcul. Les tests d'ovulation, qui détectent le pic d'hormone LH, donnent une indication plus sûre.",
    },
  ],
};

export function OvulationView() {
  const [lmp, setLmp] = useState("");
  const [cycle, setCycle] = useState("28");
  const cycleDays = Number(cycle);
  const valid =
    ISO_DAY.test(lmp) && Number.isInteger(cycleDays) && cycleDays >= 21 && cycleDays <= 45;
  return (
    <ToolLayout
      copy={OVULATION_COPY}
      eyebrow="Calculateur gratuit"
      stickers={[stickerUrl("bebe", "cigogne"), stickerUrl("bebe", "nounours")]}
      disclaimer="Résultats indicatifs, pour un cycle régulier. Ce calcul n'est pas une méthode de contraception."
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="label">
          Premier jour des dernières règles
          <input
            type="date"
            className="input"
            value={lmp}
            onChange={(event) => setLmp(event.target.value)}
          />
        </label>
        <label className="label">
          Durée du cycle (jours)
          <input
            type="number"
            min={21}
            max={45}
            className="input"
            value={cycle}
            onChange={(event) => setCycle(event.target.value)}
          />
        </label>
      </div>
      {valid ? (
        <Cycles lmp={lmp} cycleDays={cycleDays} />
      ) : (
        <p className="text-center text-muted">Le résultat s'affiche dès la date saisie.</p>
      )}
    </ToolLayout>
  );
}

function Cycles({ lmp, cycleDays }: { lmp: string; cycleDays: number }) {
  const cycles = [0, 1, 2].map((index) => {
    const start = addDays(lmp, index * cycleDays);
    const ovulation = addDays(start, cycleDays - 14);
    return {
      start,
      ovulation,
      fertileFrom: addDays(ovulation, -5),
      fertileTo: addDays(ovulation, 1),
      nextPeriod: addDays(start, cycleDays),
    };
  });
  const first = cycles[0];
  return (
    <div className="grid gap-5 border-t-[1.5px] border-line pt-6" aria-live="polite">
      {first && (
        <div className="grid justify-items-center gap-1 text-center">
          <p className="font-bold text-muted">Prochaine ovulation estimée</p>
          <p className="font-soft text-4xl font-medium text-accent-text sm:text-5xl">
            {formatDay(first.ovulation)}
          </p>
        </div>
      )}
      <p className="flex flex-wrap justify-center gap-4 text-sm font-bold">
        {(
          [
            ["bg-[#fbd5df]", "Règles"],
            ["bg-butter", "Période fertile"],
            ["bg-accent", "Ovulation"],
          ] as const
        ).map(([tone, label]) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className={`size-3.5 rounded-[4px] ${tone}`} /> {label}
          </span>
        ))}
      </p>
      <ol className="grid gap-3">
        {cycles.map((entry, index) => (
          <li key={entry.start} className="grid gap-3 rounded-2xl bg-page p-4">
            <p className="font-bold">Cycle {index + 1}</p>
            <CycleStrip cycleDays={cycleDays} />
            <div className="grid gap-2 text-sm sm:grid-cols-3">
              <span>
                <span className="text-muted">Période fertile</span>
                <br />
                <strong>
                  {formatDay(entry.fertileFrom)} au {formatDay(entry.fertileTo)}
                </strong>
              </span>
              <span>
                <span className="text-muted">Ovulation</span>
                <br />
                <strong>{formatDay(entry.ovulation)}</strong>
              </span>
              <span>
                <span className="text-muted">Règles suivantes</span>
                <br />
                <strong>{formatDay(entry.nextPeriod)}</strong>
              </span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** One square per day of the cycle: period, fertile days, ovulation. */
function CycleStrip({ cycleDays }: { cycleDays: number }) {
  const ovulationDay = cycleDays - 14;
  return (
    <div className="flex flex-wrap gap-1" aria-hidden>
      {Array.from({ length: cycleDays }, (_, day) => {
        const tone =
          day < 5
            ? "bg-[#fbd5df]"
            : day === ovulationDay
              ? "bg-accent"
              : day >= ovulationDay - 5 && day <= ovulationDay + 1
                ? "bg-butter"
                : "bg-line";
        return <span key={day} className={`size-3.5 rounded-[4px] ${tone}`} />;
      })}
    </div>
  );
}
